import {inject, Injectable, signal} from '@angular/core';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {EventMessage, EventType} from '@azure/msal-browser';
import {filter} from 'rxjs/operators';
import {protectedResources} from '../msal-config';

/**
 * UNIFIED TOKEN REFRESH AND AUTHENTICATION SERVICE
 *
 * This service centralizes all token refresh logic and authentication recovery.
 * It replaces the previous multi-layered approach (runtime recovery, session bootstrap recovery, etc.)
 * with a single, predictable flow.
 *
 * HOW AUTHENTICATION WORKS NOW:
 * ==============================
 *
 * 1. INITIAL LOGIN:
 *    - User visits app → Auth Guard checks for active MSAL account
 *    - No account → Redirect to Azure AD login
 *    - After Azure AD authentication → Redirect back to app
 *    - MSAL stores tokens in localStorage
 *    - SessionBootstrapService initializes user data
 *    - User lands on their intended page (or default start page)
 *
 * 2. NORMAL OPERATION (Tokens Valid):
 *    - MSAL Interceptor automatically attaches access tokens to API calls
 *    - Tokens refresh automatically when approaching expiry
 *    - Notification polling runs every 5 minutes (uses this service to check tokens first)
 *
 * 3. TOKEN EXPIRY DURING SESSION:
 *    - API call triggered → MSAL Interceptor detects expired token
 *    - Calls this service: tryRefreshTokenSilently()
 *    - Service attempts silent refresh using MSAL's acquireTokenSilent()
 *    - Success → API call proceeds with new token
 *    - Failure → handleTokenRefreshFailure() → Logout and redirect to Azure AD
 *
 * 4. TOKEN REFRESH FAILURE:
 *    - Silent refresh fails (e.g., refresh token expired, network issue)
 *    - Save current page URL to sessionStorage
 *    - Clear MSAL interaction locks (prevents "interaction in progress" errors)
 *    - Trigger logout and redirect to Azure AD login
 *    - After re-authentication → User returns to saved page URL
 *
 * 5. NOTIFICATION POLLING (Every 5 Minutes):
 *    - Timer triggers in UserStateService
 *    - Before fetching notifications, calls tryRefreshTokenSilently()
 *    - If refresh fails → Silently skip notification fetch (no user interruption)
 *    - Polling retries in next 5-minute cycle
 *
 * 6. CONCURRENT REFRESH ATTEMPTS:
 *    - Multiple components/services may request token refresh simultaneously
 *    - First call starts actual refresh, stores promise
 *    - Subsequent calls wait for same promise (prevents duplicate refresh attempts)
 *    - All callers receive same result
 *
 * KEY FEATURES:
 * =============
 * - Single source of truth for token refresh
 * - No recovery screen (immediate redirect to login on failure)
 * - Preserves user's page location through re-authentication
 * - Prevents race conditions with promise-based debouncing
 * - Silent failures for background operations (notification polling)
 * - Clean separation: this service handles tokens, other services handle business logic
 *
 * SIGNALS:
 * ========
 * - refreshState: 'idle' | 'refreshing' | 'failed'
 *   Can be observed by UI components if needed (currently unused)
 */
@Injectable({
  providedIn: 'root'
})
export class TokenRefreshService {
  // MSAL error codes that indicate we should attempt recovery
  private static readonly recoverableAuthErrorCodes = new Set([
    'interaction_required',
    'login_required',
    'token_refresh_required'
  ]);

  // Debounce period: after a refresh completes, wait this long before allowing another
  // Prevents rapid-fire refresh attempts that could overwhelm Azure AD
  private readonly refreshGuardMs = 10000; // 10 seconds

  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);

  // Store ongoing refresh promise to allow multiple callers to await the same refresh
  // This prevents duplicate MSAL calls when multiple components request refresh simultaneously
  private ongoingRefreshPromise: Promise<boolean> | null = null;

  // Signal for token refresh state (can be used by UI if needed)
  // Currently unused but available for future enhancements (e.g., showing refresh spinner)
  readonly refreshState = signal<'idle' | 'refreshing' | 'failed'>('idle');

  constructor() {
    this.initializeMsalEventListener();
  }

  /**
   * Initialize MSAL event subscription for token acquisition failures
   * This listener is informational only - actual recovery is handled by calling code
   */
  private initializeMsalEventListener(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((event: EventMessage) => event.eventType === EventType.ACQUIRE_TOKEN_FAILURE)
      )
      .subscribe((event: EventMessage) => {
        const errorCode = this.getMsalErrorCode(event.error);
        if (!this.shouldRecoverFromError(errorCode)) {
          return;
        }

        // Token acquisition failed with recoverable error
        // Log the warning - the calling code will decide whether to retry or force re-login
        console.warn('Token acquisition failed:', errorCode);
      });
  }

  /**
   * MAIN ENTRY POINT: Attempt silent token refresh
   *
   * Called by:
   * - SessionBootstrapService (during initialization if 401 detected)
   * - UserStateService (before notification polling every 5 minutes)
   * - AuthUnauthorizedInterceptor (on 401 for roles endpoint)
   *
   * Flow:
   * 1. Check if refresh already in progress → Wait for existing promise
   * 2. Check if MSAL account exists → Redirect to login if missing
   * 3. Perform silent token acquisition via MSAL
   * 4. Return success/failure to caller
   *
   * Promise-based debouncing ensures concurrent calls share the same refresh attempt
   *
   * @returns Promise<boolean> - true if token refresh succeeded, false otherwise
   */
  async tryRefreshTokenSilently(): Promise<boolean> {
    // If refresh is already in progress, return the ongoing promise
    // This ensures all concurrent callers wait for the same refresh attempt
    // Prevents duplicate MSAL calls and race conditions
    if (this.ongoingRefreshPromise) {
      return this.ongoingRefreshPromise;
    }

    // Check for active MSAL account (user must be authenticated)
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0]
      ?? null;

    if (!account) {
      // No account means user is not authenticated at all
      // This can happen if:
      // - User cleared browser storage
      // - Session expired completely
      // - First visit to app
      console.warn('No active MSAL account found - redirecting to login');
      this.refreshState.set('failed');
      this.handleTokenRefreshFailure(); // Immediate redirect to Azure AD
      return false;
    }

    // Create and store the refresh promise so concurrent calls can await it
    this.ongoingRefreshPromise = this.performTokenRefresh(account);

    try {
      return await this.ongoingRefreshPromise;
    } finally {
      // Clear the promise after completion and debounce period
      // This prevents infinite reuse of the same promise
      setTimeout(() => {
        this.ongoingRefreshPromise = null;
      }, this.refreshGuardMs);
    }
  }

  /**
   * Internal method to perform the actual token refresh
   *
   * Uses MSAL's acquireTokenSilent() which:
   * - First checks cache for valid token
   * - If expired, uses refresh token to get new access token
   * - If refresh token expired, throws error
   *
   * @param account - MSAL account object to refresh tokens for
   * @returns Promise<boolean> - true if refresh succeeded and token is valid
   */
  private async performTokenRefresh(account: any): Promise<boolean> {
    this.msalService.instance.setActiveAccount(account);
    this.refreshState.set('refreshing');

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        account,
        scopes: protectedResources.api.scopes,
        forceRefresh: false // Use cached token if available
      });

      // Verify we actually got a valid token
      // MSAL can return success with empty result if cache is corrupted
      if (!result || !result.accessToken) {
        console.error('Token refresh returned empty result');
        this.refreshState.set('failed');
        return false;
      }

      this.refreshState.set('idle');
      return true;
    } catch (error: any) {
      console.error('Silent token refresh failed:', error);

      // Check if this is a recoverable error or if we need to re-authenticate
      // Non-recoverable errors (no_tokens_found, no_account_error) indicate
      // the user's token cache is missing/corrupted and they need to log in again
      const errorCode = error?.errorCode?.toLowerCase();
      const isRecoverable = errorCode && !['no_tokens_found', 'no_account_error'].includes(errorCode);

      if (!isRecoverable) {
        // Token cache is missing or corrupted - force fresh login
        console.warn('Token refresh failed with non-recoverable error, redirecting to login');
        this.handleTokenRefreshFailure();
      }

      this.refreshState.set('failed');
      return false;
    }
  }

  /**
   * Handle complete token refresh failure by logging out and redirecting to Azure AD login
   *
   * This is called when:
   * - Silent token refresh fails with non-recoverable error
   * - No MSAL account exists
   * - Token cache is corrupted/missing
   *
   * Flow:
   * 1. Clear MSAL interaction locks (prevents "interaction in progress" errors)
   * 2. Save current page URL so user returns here after re-authentication
   * 3. Trigger logout if account exists, otherwise just redirect to login
   * 4. Azure AD handles authentication
   * 5. After auth, MSAL redirects back to app
   * 6. SessionBootstrapService reads saved URL and navigates user back
   *
   * Result: User is seamlessly returned to where they were after re-authentication
   */
  handleTokenRefreshFailure(): void {
    console.warn('Token refresh failed completely - triggering logout and re-login');
    this.clearMsalInteractionLocks();

    // Save current URL so user returns to their page after re-authentication
    // This is critical for UX - user doesn't lose their place in the app
    // After Azure AD auth, SessionBootstrapService.handlePostLoginRedirect()
    // reads this URL and navigates the user back to where they were
    const currentUrl = window.location.pathname + window.location.search;
    if (currentUrl && currentUrl !== '/' && currentUrl !== '') {
      sessionStorage.setItem('redirectUrl', currentUrl);
    }

    const activeAccount = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0]
      ?? null;

    if (activeAccount) {
      // Logout existing session and redirect to login
      void this.msalService.instance.logoutRedirect().catch((error: any) => {
        if (error?.errorCode !== 'interaction_in_progress') {
          console.error('MSAL logoutRedirect failed:', error);
        }
      });
      return;
    }

    // No active account - just redirect to login
    void this.msalService.instance.loginRedirect({ scopes: ['openid', 'profile'] }).catch((error: any) => {
      if (error?.errorCode !== 'interaction_in_progress') {
        console.error('MSAL loginRedirect failed:', error);
      }
    });
  }

  /**
   * Clear MSAL interaction locks from sessionStorage
   *
   * MSAL uses sessionStorage to track ongoing auth interactions
   * These locks prevent concurrent authentication attempts
   * However, they can get stuck if auth flow is interrupted
   *
   * This method cleans up stuck locks to allow fresh authentication
   */
  private clearMsalInteractionLocks(): void {
    Object.keys(sessionStorage)
      .filter(k => k.endsWith('.interaction.status'))
      .forEach(k => sessionStorage.removeItem(k));
  }

  /**
   * Check if the error code indicates a recoverable authentication error
   *
   * Recoverable errors (can retry):
   * - interaction_required: User interaction needed
   * - login_required: Re-authentication needed
   * - token_refresh_required: Token needs refresh
   *
   * Non-recoverable errors (skip):
   * - interaction_in_progress: Already authenticating
   * - null/undefined: No error code
   */
  private shouldRecoverFromError(errorCode: string | null): boolean {
    if (!errorCode || errorCode === 'interaction_in_progress') {
      return false;
    }

    return TokenRefreshService.recoverableAuthErrorCodes.has(errorCode);
  }

  /**
   * Extract error code from MSAL error object
   * MSAL errors have an errorCode property that identifies the type of error
   *
   * @param error - MSAL error object (can be any shape)
   * @returns Lowercase error code or null if not found
   */
  private getMsalErrorCode(error: unknown): string | null {
    const errorCode = (error as { errorCode?: string } | null)?.errorCode;
    return errorCode ? errorCode.toLowerCase() : null;
  }
}
